import React, { useState } from "react";
import { toast } from "sonner";
import { X, ShieldCheck, CreditCard, Lock } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { Spinner } from "./ui-bits";
import { api, CATEGORY_LABELS, CATEGORY_PRICE } from "../lib/api";

export const IssuePassportModal = ({ product, merchantId, onClose, onIssued }) => {
  const [serial, setSerial] = useState("");
  const [loading, setLoading] = useState(false);

  const pay = async () => {
    setLoading(true);
    try {
      // stash context so payment/return can complete generation
      const { data } = await api.post("/dpp/create-payment-intent", {
        product_id: product.id,
        merchant_id: merchantId,
        serial_number: serial || null,
        origin_url: window.location.origin,
      });
      localStorage.setItem("vcoin_pending_dpp", JSON.stringify({
        session_id: data.session_id,
        product_id: product.id,
        merchant_id: merchantId,
        serial_number: serial || null,
      }));
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start payment");
      setLoading(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg p-0 overflow-hidden bg-white border-navy/10 rounded-2xl" data-testid="issue-passport-modal">
        <DialogTitle className="sr-only">Issue Digital Product Passport for {product.name}</DialogTitle>
        <DialogDescription className="sr-only">Pay the tokenization fee to issue a permanent passport for this product.</DialogDescription>
        <div className="bg-navy px-7 py-5 flex items-center justify-between">
          <div className="flex items-center gap-2 text-cream">
            <ShieldCheck size={20} weight="light" className="text-gold" />
            <span className="text-sm uppercase tracking-[0.15em]">Issue Passport</span>
          </div>
          <button onClick={onClose} data-testid="modal-close" className="text-cream/60 hover:text-cream transition-colors"><X size={20} /></button>
        </div>

        <div className="p-7">
          <div className="flex gap-4">
            <div className="h-20 w-20 rounded-xl bg-cream overflow-hidden shrink-0">
              {product.image_url ? <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" /> : null}
            </div>
            <div>
              <span className="text-[0.6rem] uppercase tracking-[0.18em] text-navy/50 bg-navy/5 rounded-full px-2.5 py-1">{CATEGORY_LABELS[product.category] || product.category}</span>
              <h3 className="mt-2 text-lg text-navy leading-snug">{product.name}</h3>
              <p className="text-xs text-navy/45">{product.brand}</p>
            </div>
          </div>

          <div className="mt-6">
            <label className="text-[0.62rem] uppercase tracking-[0.2em] text-navy/50">Serial Number (optional)</label>
            <input
              data-testid="serial-number-input"
              value={serial}
              onChange={(e) => setSerial(e.target.value)}
              placeholder="e.g. VH-2024-88213"
              className="mt-2 w-full bg-cream border border-navy/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-gold transition-colors text-navy"
            />
            <p className="text-[0.68rem] text-navy/40 mt-1.5">Only the last 4 digits are ever shown publicly.</p>
          </div>

          <div className="mt-6 flex items-center justify-between bg-cream rounded-xl px-5 py-4 border border-navy/10">
            <span className="text-sm text-navy/60">Tokenization fee</span>
            <span className="font-playfair text-2xl text-navy">{CATEGORY_PRICE[product.category]}</span>
          </div>

          <button
            data-testid="pay-issue-button"
            onClick={pay}
            disabled={loading}
            className="mt-5 w-full flex items-center justify-center gap-2 bg-gold text-navy font-medium rounded-xl py-4 text-sm hover:brightness-105 active:scale-[0.99] transition-all disabled:opacity-60"
          >
            {loading ? <><Spinner className="h-4 w-4" color="#1B2B5E" /> Redirecting to secure checkout…</> : <><CreditCard size={18} weight="light" /> Pay & Issue Passport</>}
          </button>
          <p className="flex items-center justify-center gap-1.5 text-[0.68rem] text-navy/40 mt-3">
            <Lock size={12} weight="light" /> Secured by Stripe · Payment verified before issuance
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
